// @flow

import _ from 'lodash';
import type { Observable } from 'rgs-knockout';

import ko from 'rgs-knockout';
import { safer } from '../../../../../../rgs-nfd-ui-kit/components/utils/other';

import type { ExtendKoType } from '../../../../core/contracts/utils.flow';
import type { MaybeLodashLoader } from '../../../../types.flow';
import { DialogWrapper } from './dialog-wrapper';
import { unwrap } from '../../../../utils';


export interface IAdvancedDialogOptions {
	templateMode?: ExtendKoType<string>,
	id?: string,
	onDialogClose?: () => void;
	onEverySubmit?: () => void;
	html?: string,
	css?: {
		[string]: boolean
	}
}

export interface ICanCommunicateWithParent<P, A> {
	getDialogPayloadData (): P;

	getDialogActions (): A;
}

export class AdvancedDialog extends DialogWrapper {
	__isFetchingFlag: Observable<boolean> = ko.observable(false);

	__submitDialogChanges: boolean;

	constructor (options: IAdvancedDialogOptions = {}) {
		super();

		this._options = options;
		this.__submitDialogChanges = false;
	}

	_initDialogWithOptions (options: IAdvancedDialogOptions) {
		const html = unwrap(options.html || this.getDialogTemplate());
		const mode = unwrap(options.templateMode);

		const modal = this.create({
			templateMode: mode,
			template: {
				html: html,
				data: this,
			},
			css: options.css,
			containerCss: options.containerCss,
		});

		modal.once('hidden', (): void => {
			this._selectCallback('onDialogClose')(this.hasDialogChanges());
			this.dispose();
		});


		return modal;
	}

	_getDialogOptions (): IAdvancedDialogOptions {
		return _.defaultsDeep(this._options, this.getDialogOptions(), {
			onDialogClose: _.identity,
			templateMode: 'nfd-content',
			css: {},
			html: '',
		});
	}

	showDialog = (): void => {

		if (!this._modal) {
			this._modal = this._initDialogWithOptions(this._getDialogOptions());
		}

		this.show(this._modal);
	};

	hideDialog = (): void => {
		this.hide(this._modal);
	};

	callSubmitCallbackWithData<P> (payload: P): Promise<void> {
		return this.submit(this.getDialogPayloadData(payload), this.getDialogActions());
	}

	getSourceIsFetchingFlag (): Observable<boolean> {
		return this.__isFetchingFlag;
	}

	async submitWithIsFetchingFlag (callback: () => Promise<void>): Promise<void> {
		this.__isFetchingFlag(true);

		await safer(callback)(this.getDialogPayloadData(), this.getDialogActions());

		this.setDialogSubmitChanges(true);

		this.__isFetchingFlag(false);
	}

	setDialogSubmitChanges (status: boolean): void {
		this._selectCallback('onEverySubmit')();
		this.__submitDialogChanges = status;
	}

	_selectCallback (path: string): Function {
		return _.get(this._options, path) || _.identity;
	}

	/* virtual */
	hasDialogChanges (): boolean {
		return this.__submitDialogChanges;
	}

	/* virtual */
	getDialogTemplate (): MaybeLodashLoader {
		return '';
	}

	/* virtual */
	getDialogOptions (): ?IAdvancedDialogOptions {
		// override me
	}


	// eslint-disable-next-line no-unused-vars
	async submit<P, A> (payload: P, actions: A): Promise<any> {
		// override me
	}

	/* virtual */
	getDialogPayloadData () {
		// override me
	}

	/* virtual */
	getDialogActions () {
		// override me
	}

	/* virtual */
	dispose (): void {
		// override me, my friend
	}
}
